import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const Curriculum = () => {
  const [curriculums, setCurriculums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [gradeFilter, setGradeFilter] = useState('All');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/admin/api/curriculum.php');
        const result = await res.json();
        if (result.success) setCurriculums(result.data || []);
        else setError('Failed to load curriculum data');
      } catch {
        setError('Unable to load curriculum at this time.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = ['All', ...new Set(curriculums.map(c => c.category).filter(Boolean))];
  const grades = ['All', ...new Set(curriculums.map(c => c.grade).filter(Boolean))];

  const filtered = curriculums.filter(c => {
    const catOk = categoryFilter === 'All' || c.category === categoryFilter;
    const gradeOk = gradeFilter === 'All' || c.grade === gradeFilter;
    return catOk && gradeOk;
  });

  const CATEGORY_COLORS = {
    'Early Years': 'bg-pink-100 text-pink-700 border border-pink-200',
    'Primary': 'bg-primary/10 text-primary border border-primary/20',
    'Middle': 'bg-green-100 text-green-700 border border-green-200',
  };

  return (
    <div className="page-enter">
      <Helmet>
        <title>Curriculum — PAK School &amp; College System | Educational Programs</title>
        <meta name="description" content="Explore PAK School & College System's comprehensive curriculum designed to foster academic excellence, critical thinking, and character development." />
        <link rel="canonical" href="https://pakschoolandcollege.com/curriculum" />
      </Helmet>

      <div className="page-hero">
        <div className="page-hero-content">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-5">
            📋 Academic Framework
          </div>
          <h1>Academic <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Curriculum</span></h1>
          <p>Explore our comprehensive curriculum designed to provide quality education across all grade levels</p>
        </div>
      </div>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Filters */}
          {!loading && !error && curriculums.length > 0 && (
            <div className="bg-gray-50 rounded-2xl p-5 mb-8 flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-navy font-poppins font-600 text-sm mb-2">Category</label>
                <select
                  value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-inter focus:outline-none focus:border-primary bg-white"
                >
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-navy font-poppins font-600 text-sm mb-2">Grade</label>
                <select
                  value={gradeFilter}
                  onChange={e => setGradeFilter(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-inter focus:outline-none focus:border-primary bg-white"
                >
                  {grades.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center py-24 gap-4">
              <div className="spinner" />
              <p className="text-gray-400 text-sm font-inter">Loading curriculum...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-exclamation-triangle text-accent text-2xl" />
              </div>
              <h3 className="font-poppins font-700 text-gray-700 text-lg mb-2">{error}</h3>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-graduation-cap text-gray-300 text-2xl" />
              </div>
              <h3 className="font-poppins font-700 text-gray-600 text-lg mb-2">No curriculum found</h3>
              <p className="text-gray-400 font-inter text-sm">Try adjusting filters to see more results.</p>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <>
              <p className="text-gray-400 font-inter text-sm mb-6">Showing {filtered.length} curriculum item{filtered.length !== 1 ? 's' : ''}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filtered.map((curr, i) => (
                  <div key={curr.id || i} className="group bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <i className="fas fa-layer-group text-primary" />
                      </div>
                      <span className={`text-xs font-poppins font-700 px-2.5 py-1 rounded-full ${CATEGORY_COLORS[curr.category] || 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                        {curr.category}
                      </span>
                    </div>
                    <h3 className="font-poppins font-700 text-navy text-base mb-0.5">{curr.grade}</h3>
                    {curr.subject && <p className="text-gray-500 font-inter text-sm">{curr.subject}</p>}
                    {curr.title && <p className="text-gray-400 font-inter text-xs mt-0.5">{curr.title}</p>}
                    {curr.academic_year && <p className="text-gray-400 font-inter text-xs mt-1">AY {curr.academic_year}</p>}
                    {curr.description && (
                      <p className="text-gray-500 font-inter text-xs mt-3 leading-relaxed line-clamp-3">{curr.description}</p>
                    )}
                    {curr.file && (
                      <a
                        href={`/admin/assets/curriculum/${curr.file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 flex items-center justify-center gap-1.5 w-full py-2 rounded-xl bg-primary text-white text-xs font-poppins font-600 hover:bg-primary-dark transition-colors"
                      >
                        <i className="fas fa-download" /> Download
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Info section */}
          {!loading && !error && (
            <div className="mt-14 grid sm:grid-cols-3 gap-5">
              {[
                { icon: 'fa-book-open', title: 'Comprehensive', desc: 'Our curriculum covers all essential subjects and learning objectives for each grade level.', color: 'bg-primary/10 text-primary' },
                { icon: 'fa-users', title: 'Student-Centered', desc: 'Designed to meet the diverse learning needs and developmental stages of our students.', color: 'bg-green-100 text-green-600' },
                { icon: 'fa-chart-line', title: 'Progressive', desc: 'Structured to build upon previous knowledge and prepare students for future learning.', color: 'bg-amber-100 text-amber-600' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-gray-50 rounded-2xl">
                  <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`}>
                    <i className={`fas ${item.icon}`} />
                  </div>
                  <div>
                    <h4 className="font-poppins font-700 text-navy text-sm mb-1">{item.title}</h4>
                    <p className="text-gray-500 font-inter text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Curriculum;
